/**
 * useIsNativeApp — v147.99.20
 *
 * Returns `true` when the React app is running inside the Capacitor
 * iOS / Android shell (webview), `false` when it's running in a
 * regular browser tab. Screens use this to strip marketing chrome
 * (Landing header + "Back to home" links + external footer) so the
 * flow reads like a native onboarding — no browser bar peek, no
 * "back to the marketing site" affordance that leads nowhere on
 * iOS/Android.
 *
 * Safe on SSR (`window` undefined) and safe on older Capacitor
 * versions that don't expose the helper (falls back to `false`).
 */
import { useEffect, useState } from "react";

function detectNative() {
  if (typeof window === "undefined") return false;
  try {
    return !!window.Capacitor?.isNativePlatform?.();
  } catch {
    return false;
  }
}

export function useIsNativeApp() {
  const [isNative, setIsNative] = useState(detectNative);
  // Capacitor injects the global BEFORE React mounts, so a single
  // synchronous read is enough. We keep the effect purely defensive
  // for the rare case where a plugin bootstraps late.
  useEffect(() => {
    const v = detectNative();
    if (v !== isNative) setIsNative(v);
  }, [isNative]);
  return isNative;
}

export default useIsNativeApp;
